import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  runTransaction,
  Unsubscribe,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../../../shared/storage/firebase';
import {
  PortfolioAiVersion,
  PortfolioAiGroup,
  PortfolioEntry,
  PortfolioPhoto,
  PortfolioSessionContext,
  deriveEntryStatus,
  isAttachedAiCreation,
  visibleAiCreations,
  PortfolioAiCreationType,
  portfolioClassKey,
} from './types';
import { applyGroupConfiguration, GroupAssignment, groupOptions, groupIsRevealed } from './aiGroups';
import { posterRequiredForType, sourceFilename } from './aiCreation';

function entriesCol(squadId: string) {
  return collection(db, 'squads', squadId, 'portfolioEntries');
}

function entryRef(squadId: string, entryId: string) {
  return doc(db, 'squads', squadId, 'portfolioEntries', entryId);
}

function newId(): string {
  return doc(collection(db, '_')).id;
}

function contentTypeForUpload(file: File | Blob, fallbackExt?: string): string {
  const typed = String((file as File).type || '').trim();
  if (typed) return typed;
  const name = file instanceof File ? file.name : '';
  const ext = (fallbackExt || name.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg'].includes(ext)) return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'mp4') return 'video/mp4';
  if (ext === 'mov') return 'video/quicktime';
  if (ext === 'webm') return 'video/webm';
  const audioMime: Record<string, string> = { mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4', aac: 'audio/aac', flac: 'audio/flac', opus: 'audio/ogg' };
  if (audioMime[ext]) return audioMime[ext];
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'glb') return 'model/gltf-binary';
  if (ext === 'gltf') return 'model/gltf+json';
  return 'application/octet-stream';
}

/** Firestore rejects `undefined` anywhere in a document (including nested arrays). */
function omitUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      out[key] = value.map((item) =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? omitUndefined(item as Record<string, unknown>)
          : item
      );
      continue;
    }
    if (value && typeof value === 'object' && !(value instanceof Date)) {
      out[key] = omitUndefined(value as Record<string, unknown>);
      continue;
    }
    out[key] = value;
  }
  return out as T;
}

/** Deterministic id so team+session lookups need no composite index. */
export function portfolioEntryId(teamId: string, sessionId: string): string {
  const safeSession = sessionId.replace(/\//g, '_');
  return `${teamId}__${safeSession}`;
}

export interface AddAiCreationArgs {
  squadId: string;
  entryId: string;
  teamId: string;
  sessionId: string;
  type: PortfolioAiCreationType;
  url?: string | null;
  file?: File | null;
  posterFile?: Blob | File | null;
  posterUrl?: string | null;
  filename?: string | null;
  caption?: string | null;
  sourcePhotoIds: string[];
  uploadedBy: string;
  uploadedByName: string;
  revealed?: boolean;
  groupId?: string;
  optionOrder?: number;
  creationId?: string;
  onProgress?: (percent: number) => void;
  nominateForLibrary?: boolean;
  sourceLesson?: { sessionId: string; levelId: string; slotIndex?: number; date?: string };
  libraryContentType?: 'character' | 'world' | 'movie' | 'comic' | 'poster' | 'game' | 'audio' | 'model_3d';
  squadName?: string;
  teamName?: string;
}

export class ClassroomPortfolioService {
  static subscribe(
    squadId: string,
    onData: (entries: PortfolioEntry[]) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    const q = query(entriesCol(squadId), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        const entries = snap.docs.map((d) => {
          const data = d.data() as Omit<PortfolioEntry, 'id'>;
          return { ...data, id: d.id, squadId } as PortfolioEntry;
        });
        onData(entries);
      },
      (err) => onError?.(err as Error)
    );
  }

  static async getEntry(
    squadId: string,
    teamId: string,
    sessionId: string
  ): Promise<PortfolioEntry | null> {
    const id = portfolioEntryId(teamId, sessionId);
    const snap = await getDoc(entryRef(squadId, id));
    if (!snap.exists()) return null;
    return { ...(snap.data() as Omit<PortfolioEntry, 'id'>), id: snap.id, squadId };
  }

  private static async findEntryForClass(args: {
    squadId: string;
    teamId: string;
    session: PortfolioSessionContext;
  }): Promise<PortfolioEntry | null> {
    // Compatibility for work created before class entries used one schedule key.
    const snap = await getDocs(query(entriesCol(args.squadId), where('teamId', '==', args.teamId)));
    const wantedKey = portfolioClassKey({ teamId: args.teamId, ...args.session });
    const matches = snap.docs
      .map((item) => ({ ...(item.data() as Omit<PortfolioEntry, 'id'>), id: item.id, squadId: args.squadId }))
      .filter((entry) => portfolioClassKey(entry) === wantedKey)
      .sort((a, b) => {
        const contentA = (a.photos?.length || 0) + (a.aiVersions?.length || 0);
        const contentB = (b.photos?.length || 0) + (b.aiVersions?.length || 0);
        return contentB - contentA || a.createdAt - b.createdAt;
      });
    return matches[0] || null;
  }

  /** Create entry if missing; returns entry id. */
  static async ensureEntry(args: {
    squadId: string;
    teamId: string;
    memberIdsSnapshot: string[];
    session: PortfolioSessionContext;
  }): Promise<string> {
    const matching = await this.findEntryForClass(args);
    if (matching) return matching.id;
    const id = portfolioEntryId(args.teamId, args.session.sessionId);
    const ref = entryRef(args.squadId, id);
    const existing = await getDoc(ref);
    if (existing.exists()) return id;

    const now = Date.now();
    const payload = omitUndefined({
      squadId: args.squadId,
      teamId: args.teamId,
      sessionId: args.session.sessionId,
      levelId: args.session.levelId,
      levelTitle: args.session.levelTitle,
      slotIndex: args.session.slotIndex,
      date: args.session.date,
      memberIdsSnapshot: [...args.memberIdsSnapshot],
      photos: [],
      aiVersions: [],
      teacherNote: null,
      status: 'draft' as const,
      createdAt: now,
      updatedAt: now,
    });
    await setDoc(ref, payload);
    return id;
  }

  static async addPhoto(args: {
    squadId: string;
    teamId: string;
    memberIdsSnapshot: string[];
    session: PortfolioSessionContext;
    file: File;
    uploadedBy: string;
    attributedStudentIds?: string[];
  }): Promise<{ entryId: string; photo: PortfolioPhoto }> {
    const entryId = await this.ensureEntry({
      squadId: args.squadId,
      teamId: args.teamId,
      memberIdsSnapshot: args.memberIdsSnapshot,
      session: args.session,
    });

    const photoId = newId();
    const ext =
      (args.file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') ||
      'jpg';
    const storagePath = `squads/${args.squadId}/portfolios/${args.teamId}/${args.session.sessionId.replace(/\//g, '_')}/${photoId}.${ext}`;
    const sRef = storageRef(storage, storagePath);
    await uploadBytes(sRef, args.file, {
      contentType: args.file.type || 'image/jpeg',
    });
    const url = await getDownloadURL(sRef);

    const photo = omitUndefined({
      id: photoId,
      url,
      storagePath,
      uploadedAt: Date.now(),
      uploadedBy: args.uploadedBy,
      attributedStudentIds: args.attributedStudentIds,
    }) as PortfolioPhoto;

    const entrySnap = await getDoc(entryRef(args.squadId, entryId));
    const entry = entrySnap.exists()
      ? { ...(entrySnap.data() as Omit<PortfolioEntry, 'id'>), id: entrySnap.id, squadId: args.squadId }
      : null;
    const photos = [...(entry?.photos || []), photo].map((p) =>
      omitUndefined({ ...p } as Record<string, unknown>)
    );
    const aiVersions = (entry?.aiVersions || []).map((a) =>
      omitUndefined({ ...a } as Record<string, unknown>)
    );
    const status = deriveEntryStatus({
      photos: photos as unknown as PortfolioPhoto[],
      aiVersions: aiVersions as unknown as PortfolioAiVersion[],
    });

    await updateDoc(
      entryRef(args.squadId, entryId),
      omitUndefined({
        photos,
        aiVersions,
        status,
        memberIdsSnapshot: args.memberIdsSnapshot,
        levelId: args.session.levelId,
        levelTitle: args.session.levelTitle,
        slotIndex: args.session.slotIndex,
        date: args.session.date,
        updatedAt: Date.now(),
      })
    );

    return { entryId, photo };
  }

  static async setTeacherNote(args: {
    squadId: string;
    entryId: string;
    teacherNote: string;
  }): Promise<void> {
    await updateDoc(entryRef(args.squadId, args.entryId), {
      teacherNote: args.teacherNote,
      updatedAt: Date.now(),
    });
  }

  static async setPhotosArchived(args: {
    squadId: string;
    entryId: string;
    photoIds: string[];
    archived: boolean;
  }): Promise<void> {
    const ids = new Set(args.photoIds.filter(Boolean));
    if (!ids.size) return;
    const ref = entryRef(args.squadId, args.entryId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Portfolio entry not found.');
    const entry = snap.data() as Omit<PortfolioEntry, 'id'>;
    const now = Date.now();
    const photos = (entry.photos || []).map((photo) => {
      if (!ids.has(photo.id)) return photo;
      if (args.archived) return { ...photo, archived: true, archivedAt: now };
      const restored = { ...photo };
      delete restored.archived;
      delete restored.archivedAt;
      return restored;
    });
    const status = deriveEntryStatus({
      photos: photos.filter((photo) => !photo.archived),
      aiVersions: entry.aiVersions || [],
    });
    await updateDoc(ref, { photos, status, updatedAt: now });
  }

  static async movePhotos(args: {
    squadId: string;
    sourceEntryId: string;
    photoIds: string[];
    destinationTeamId: string;
    destinationMemberIdsSnapshot: string[];
    destinationSession: PortfolioSessionContext;
  }): Promise<void> {
    const ids = new Set(args.photoIds.filter(Boolean));
    if (!ids.size) return;
    const destinationEntryId = await this.ensureEntry({
      squadId: args.squadId,
      teamId: args.destinationTeamId,
      memberIdsSnapshot: args.destinationMemberIdsSnapshot,
      session: args.destinationSession,
    });
    if (destinationEntryId === args.sourceEntryId) return;

    const sourceRef = entryRef(args.squadId, args.sourceEntryId);
    const destinationRef = entryRef(args.squadId, destinationEntryId);
    await runTransaction(db, async (transaction) => {
      const [sourceSnap, destinationSnap] = await Promise.all([
        transaction.get(sourceRef),
        transaction.get(destinationRef),
      ]);
      if (!sourceSnap.exists() || !destinationSnap.exists()) {
        throw new Error('Portfolio entry not found.');
      }
      const source = sourceSnap.data() as Omit<PortfolioEntry, 'id'>;
      const destination = destinationSnap.data() as Omit<PortfolioEntry, 'id'>;
      const moved = (source.photos || []).filter((photo) => ids.has(photo.id));
      if (!moved.length) return;
      const sourcePhotos = (source.photos || []).filter((photo) => !ids.has(photo.id));
      const existingIds = new Set((destination.photos || []).map((photo) => photo.id));
      const destinationPhotos = [
        ...(destination.photos || []),
        ...moved.filter((photo) => !existingIds.has(photo.id)),
      ];
      const now = Date.now();
      transaction.update(sourceRef, {
        photos: sourcePhotos,
        status: deriveEntryStatus({
          photos: sourcePhotos.filter((photo) => !photo.archived),
          aiVersions: source.aiVersions || [],
        }),
        updatedAt: now,
      });
      transaction.update(destinationRef, {
        photos: destinationPhotos,
        status: deriveEntryStatus({
          photos: destinationPhotos.filter((photo) => !photo.archived),
          aiVersions: destination.aiVersions || [],
        }),
        updatedAt: now,
      });
    });
  }

  static async updateAiVersions(args: {
    squadId: string;
    entryId: string;
    photos: PortfolioPhoto[];
    aiVersions: PortfolioAiVersion[];
  }): Promise<void> {
    const status = deriveEntryStatus({
      photos: args.photos,
      aiVersions: args.aiVersions,
    });
    await updateDoc(entryRef(args.squadId, args.entryId), {
      aiVersions: args.aiVersions,
      status,
      updatedAt: Date.now(),
    });
  }

  /** Mark / create a stub AI version as approved (until AI pipeline exists). */
  static async markAiReady(args: {
    squadId: string;
    entryId: string;
    photos: PortfolioPhoto[];
    aiVersions: PortfolioAiVersion[];
  }): Promise<void> {
    const existing = [...(args.aiVersions || [])];
    const now = Date.now();
    if (!existing.length) {
      existing.push({
        id: newId(),
        status: 'approved',
        createdAt: now,
        updatedAt: now,
      });
    } else {
      existing[0] = {
        ...existing[0],
        status: 'approved',
        updatedAt: now,
      };
    }
    await this.updateAiVersions({
      squadId: args.squadId,
      entryId: args.entryId,
      photos: args.photos,
      aiVersions: existing,
    });
  }

  static async markRevealed(args: {
    squadId: string;
    entryId: string;
    photos: PortfolioPhoto[];
    aiVersions: PortfolioAiVersion[];
  }): Promise<void> {
    const now = Date.now();
    const visible = visibleAiCreations(args.aiVersions);
    if (!visible.length) {
      throw new Error('Add an AI creation before marking revealed.');
    }
    const existing = (args.aiVersions || []).map((a) =>
      visibleAiCreations([a]).length
        ? { ...a, status: 'revealed' as const, updatedAt: now }
        : a
    );
    await this.updateAiVersions({
      squadId: args.squadId,
      entryId: args.entryId,
      photos: args.photos,
      aiVersions: existing,
    });
  }

  static async completeReveal(args: {
    squadId: string;
    entryId: string;
    photos: PortfolioPhoto[];
    aiVersions: PortfolioAiVersion[];
    guessText?: string;
  }): Promise<void> {
    if (!visibleAiCreations(args.aiVersions).length) throw new Error('This entry has no creation to reveal.');
    const byEntry = new Map<string, Set<string>>([[args.entryId, new Set()]]);
    args.aiVersions.forEach(ai => {
      const sourceId = ai.originEntryId || args.entryId;
      if (!byEntry.has(sourceId)) byEntry.set(sourceId, new Set());
      byEntry.get(sourceId)!.add(ai.id);
    });
    await runTransaction(db, async tx => {
      const sources = await Promise.all(Array.from(byEntry, async ([id, ids]) => {
        const target = entryRef(args.squadId, id);
        return { id, ids, target, snap: await tx.get(target) };
      }));
      const now = Date.now();
      for (const { id, ids, target, snap } of sources) {
        if (!snap.exists()) throw new Error('Portfolio entry not found.');
        const current = snap.data() as PortfolioEntry;
        const aiVersions = current.aiVersions.map(item => ids.has(item.id) && visibleAiCreations([item]).length
          ? { ...item, status: 'revealed' as const, updatedAt: now } : item);
        tx.update(target, { aiVersions, status: deriveEntryStatus({ photos: current.photos, aiVersions }),
          ...(id === args.entryId ? { guess_text: String(args.guessText || '').trim() || null } : {}),
          revealed_at: now, updatedAt: now });
      }
    });
  }

  /** Move revealed (or any AI state) back to approved / AI ready. */
  static async revertToAiReady(args: {
    squadId: string;
    entryId: string;
    photos: PortfolioPhoto[];
    aiVersions: PortfolioAiVersion[];
  }): Promise<void> {
    const now = Date.now();
    const existing = (args.aiVersions || []).map((a) =>
      visibleAiCreations([a]).length
        ? { ...a, status: 'approved' as const, updatedAt: now }
        : a
    );
    await this.updateAiVersions({
      squadId: args.squadId,
      entryId: args.entryId,
      photos: args.photos,
      aiVersions: existing,
    });
  }

  static async addAiCreation(args: AddAiCreationArgs): Promise<PortfolioAiVersion> {
    const snap = await getDoc(entryRef(args.squadId, args.entryId));
    if (!snap.exists()) {
      throw new Error('Portfolio entry not found.');
    }
    const entry = { ...(snap.data() as Omit<PortfolioEntry, 'id'>), id: snap.id };
    const genId = args.creationId || newId();
    const existingCreation = entry.aiVersions?.find(ai => ai.id === genId);
    if (existingCreation) return existingCreation;
    const now = Date.now();
    const sessionKey = (args.sessionId || entry.sessionId || 'session').replace(/\//g, '_');
    const basePath = `squads/${args.squadId}/portfolios/${args.teamId}/${sessionKey}/gens/${genId}`;

    let url = String(args.url || '').trim() || undefined;
    let storagePath: string | undefined;
    if (args.file) {
      const ext =
        (args.file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '') ||
        'bin';
      storagePath = `${basePath}/asset.${ext}`;
      const sRef = storageRef(storage, storagePath);
      const upload = uploadBytesResumable(sRef, args.file, {
        contentType: contentTypeForUpload(args.file, ext),
      });
      await new Promise<void>((resolve, reject) => upload.on(
        'state_changed',
        (snapshot) => args.onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 92)),
        reject,
        resolve
      ));
      url = await getDownloadURL(sRef);
    }

    let posterUrl = String(args.posterUrl || '').trim() || undefined;
    let posterStoragePath: string | undefined;
    if (args.type === 'image' && url && !args.posterFile && !posterUrl) {
      posterUrl = url;
    }
    if (args.posterFile) {
      args.onProgress?.(94);
      const posterName =
        args.posterFile instanceof File && args.posterFile.name.includes('.')
          ? args.posterFile.name.split('.').pop()
          : 'jpg';
      const ext = (posterName || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      posterStoragePath = `${basePath}/poster.${ext}`;
      const pRef = storageRef(storage, posterStoragePath);
      await uploadBytes(pRef, args.posterFile, {
        contentType: contentTypeForUpload(args.posterFile, ext),
      });
      posterUrl = await getDownloadURL(pRef);
    }

    if (!url) throw new Error('Add a URL or upload a file.');
    if (!posterUrl && posterRequiredForType(args.type)) {
      throw new Error('A poster image is required.');
    }

    const gen = omitUndefined({
      id: genId,
      url,
      storagePath,
      posterUrl,
      posterStoragePath,
      filename: String(args.filename || sourceFilename(args.file, url)).trim() || undefined,
      type: args.type,
      caption: String(args.caption || '').trim() || undefined,
      sourcePhotoIds: [...(args.sourcePhotoIds || [])],
      groupId: args.groupId,
      optionOrder: args.optionOrder,
      uploadedBy: args.uploadedBy,
      uploadedByName: args.uploadedByName,
      uploadedAt: now,
      status: args.revealed ? ('revealed' as const) : ('approved' as const),
      createdAt: now,
      updatedAt: now,
    }) as PortfolioAiVersion;

    const target = entryRef(args.squadId, args.entryId);
    await runTransaction(db, async tx => {
      const fresh = await tx.get(target);
      if (!fresh.exists()) throw new Error('Portfolio entry not found.');
      const current = fresh.data() as PortfolioEntry;
      if (current.aiVersions.some(ai => ai.id === genId)) return;
      if (args.groupId && !current.aiGroups?.some(g => g.id === args.groupId)) {
        throw new Error('This group was removed. Reopen the editor and assign the file again.');
      }
      const aiVersions = [...current.aiVersions, gen];
      const aiGroups = (current.aiGroups || []).map(g => g.id === args.groupId ? { ...g, reviewed: false } : g);
      tx.update(target, omitUndefined({ aiVersions, aiGroups,
        status: deriveEntryStatus({ photos: current.photos, aiVersions }), updatedAt: Date.now() }));
    });

    args.onProgress?.(100);

    return gen;
  }

  static async configureAiGroups(args: {
    squadId: string; entryId: string; groups: PortfolioAiGroup[];
    assignments: GroupAssignment[]; expectedRevision: number;
  }): Promise<void> {
    const target = entryRef(args.squadId, args.entryId);
    await runTransaction(db, async tx => {
      const snap = await tx.get(target);
      if (!snap.exists()) throw new Error('Portfolio entry not found.');
      const entry = snap.data() as PortfolioEntry;
      if ((entry.aiGroupsRevision || 0) !== args.expectedRevision) {
        throw new Error('Someone else edited these groups. Close and reopen the editor to load their changes.');
      }
      const updated = applyGroupConfiguration(entry, args.groups, args.assignments);
      tx.update(target, omitUndefined({ ...updated, aiGroupsRevision: args.expectedRevision + 1,
        status: deriveEntryStatus({ photos: entry.photos, aiVersions: updated.aiVersions }), updatedAt: Date.now() }));
    });
  }

  static async reviewAiGroup(args: {
    squadId: string; entryId: string; groupId: string; updatedBy: string;
    favoriteId?: string | null; notes?: string; reviewed?: boolean; reveal?: boolean;
  }): Promise<void> {
    const target = entryRef(args.squadId, args.entryId);
    await runTransaction(db, async tx => {
      const snap = await tx.get(target);
      if (!snap.exists()) throw new Error('Portfolio entry not found.');
      const entry = snap.data() as PortfolioEntry;
      const group = entry.aiGroups?.find(g => g.id === args.groupId);
      if (!group) throw new Error('This group no longer exists.');
      const options = groupOptions(entry, group.id);
      if (!options.length) throw new Error('Wait for this group’s uploads to finish.');
      if (!args.reveal && !groupIsRevealed(options)) throw new Error('Reveal this group before reviewing it.');
      const favoriteId = args.favoriteId !== undefined ? args.favoriteId : group.favoriteId;
      if (favoriteId && !options.some(ai => ai.id === favoriteId)) throw new Error('This option is no longer available.');
      if (args.reviewed && options.length > 1 && !favoriteId) throw new Error('Star a favorite or choose Decide later.');
      const now = Date.now();
      const aiVersions = args.reveal ? entry.aiVersions.map(ai => options.some(o => o.id === ai.id)
        ? { ...ai, status: 'revealed' as const, updatedAt: now } : ai) : entry.aiVersions;
      const aiGroups = (entry.aiGroups || []).map(g => g.id !== group.id ? g : {
        ...g,
        ...(args.favoriteId !== undefined ? { favoriteId: args.favoriteId, reviewed: false } : {}),
        ...(args.notes !== undefined ? { notes: args.notes.slice(0, 4000), reviewed: false } : {}),
        ...(args.reviewed !== undefined ? { reviewed: args.reviewed } : {}),
        updatedAt: now, updatedBy: args.updatedBy,
      });
      tx.update(target, { aiVersions, aiGroups, status: deriveEntryStatus({ photos: entry.photos, aiVersions }), updatedAt: now });
    });
  }

  static async setAiCreationsArchived(args: {
    squadId: string;
    entryId: string;
    photos: PortfolioPhoto[];
    aiVersions: PortfolioAiVersion[];
    genIds: string[];
    archived: boolean;
  }): Promise<void> {
    const ids = new Set(args.genIds.filter(Boolean));
    if (!ids.size) return;
    const byEntry = new Map<string, Set<string>>();
    ids.forEach(id => {
      const sourceId = args.aiVersions.find(ai => ai.id === id)?.originEntryId || args.entryId;
      if (!byEntry.has(sourceId)) byEntry.set(sourceId, new Set());
      byEntry.get(sourceId)!.add(id);
    });
    await runTransaction(db, async tx => {
      const sources = await Promise.all(Array.from(byEntry, async ([id, sourceIds]) => {
        const target = entryRef(args.squadId, id);
        return { target, sourceIds, snap: await tx.get(target) };
      }));
      for (const { target, sourceIds, snap } of sources) {
        if (!snap.exists()) throw new Error('Portfolio entry not found.');
        const current = snap.data() as PortfolioEntry;
        const now = Date.now();
        const aiVersions = current.aiVersions.map(ai => {
          if (!sourceIds.has(ai.id) || !isAttachedAiCreation(ai)) return ai;
          const next = { ...ai, updatedAt: now };
          if (args.archived) return { ...next, archived: true, archivedAt: now };
          delete next.archived; delete next.archivedAt;
          return next;
        });
        const aiGroups = (current.aiGroups || []).map(group => {
          if (!current.aiVersions.some(ai => sourceIds.has(ai.id) && ai.groupId === group.id)) return group;
          return { ...group, reviewed: false,
            favoriteId: args.archived && group.favoriteId && sourceIds.has(group.favoriteId) ? null : group.favoriteId || null };
        });
        tx.update(target, { aiVersions, aiGroups, status: deriveEntryStatus({ photos: current.photos, aiVersions }), updatedAt: now });
      }
    });
  }

  /** Remove AI status so the entry is photos-only again. */
  static async clearAiStatus(args: {
    squadId: string;
    entryId: string;
    photos: PortfolioPhoto[];
  }): Promise<void> {
    await this.updateAiVersions({
      squadId: args.squadId,
      entryId: args.entryId,
      photos: args.photos,
      aiVersions: [],
    });
  }
}
